import {Avatar, Button, Card, DataStatus, IconButton} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Text, ValueByDevice, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetEmployeeWorkProfessionsListQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {getActiveOptions} from '../../utils/getActiveOptions';
import {RecordAuditParticipantMember} from './RecordAuditParticipantMember';
import {RecordAuditTeamMember} from './RecordAuditTeamMember';

export interface TeamParticipant {
  employeeId: string;
}

interface TeamMemberPlaceholderProps extends TestIdProps {
  /** Link label, e.g. "Select auditor" / "Add participant". */
  title: string;
  onClick: () => void;
}

// Empty-state affordance for a team slot (auditor / responsible person / participants):
// a blue dashed tile matching a RecordAuditTeamMember card — same padding + border, and a
// 56px content row so the empty tile is exactly as tall as a filled member card (avatar +
// Flag + name). Mirrors the header snippet placeholder / photo upload card rather than a
// bare "No data" status.
const PLACEHOLDER_CONTENT_HEIGHT = 14;

function TeamMemberPlaceholder(props: TeamMemberPlaceholderProps) {
  return (
    <Box
      padding={3}
      height="100%"
      borderRadius="small"
      border="1px dashed"
      borderColor="palettes.blue.60.100"
      backgroundColor="palettes.blue.10.100"
      data-testid={suffixTestId('placeholder', props)}
    >
      {/* Fill the (grid-stretched) card height and center the content, so the empty-state row
          lines up with a taller filled team-member card next to it instead of sticking to the top. */}
      <HStack spacing={3} align="center" height="100%" minHeight={PLACEHOLDER_CONTENT_HEIGHT}>
        <Avatar icon="social/person_add" size="default" />
        <Button
          variant="link"
          title={props.title}
          onClick={props.onClick}
          data-testid={suffixTestId('placeholder-button', props)}
        />
      </HStack>
    </Box>
  );
}

// Detail page default: its content column is width-capped, so cards stay wide enough.
const DEFAULT_GRID_COLUMNS: ValueByDevice<number> = [1, 2, 4];

interface RecordAuditParticipationProps extends TestIdProps {
  /**
   * `card` (default) wraps the panel in its own Card — the audit detail page layout. `flat`
   * renders the content bare for hosts that already provide card chrome (e.g. the service
   * order Audits tab), so the team panel doesn't nest a card inside a card.
   */
  variant?: 'card' | 'flat';
  /**
   * Member-card grid columns per device. Containers narrower than the detail page (e.g. the
   * service order Audits tab) pass fewer columns so a card never drops under its min-content
   * width (avatar + role chip + action icon ≈ 240px).
   */
  columns?: number | ValueByDevice<number>;
  auditorEmployeeId: string | null;
  responsiblePersonEmployeeId: string | null;
  participants: ReadonlyArray<TeamParticipant>;
  // Employee id -> display name, built from the audit response (auditor, responsible
  // person, participants). Names come from the response so we don't fetch the whole user list.
  nameByEmployeeId: ReadonlyMap<string, string>;
  isEditable: boolean;
  // True while a header update is changing this slot — the card shows a loading skeleton straight
  // away instead of flashing the placeholder until the audit refetch resolves the new name.
  isAuditorUpdating: boolean;
  isResponsiblePersonUpdating: boolean;
  // Number of just-added participants whose names haven't landed yet — rendered as loading cards.
  loadingParticipantCount: number;
  onAddParticipant: () => void;
  onRemoveParticipant: (index: number) => void;
  onEditAuditor: () => void;
  onEditResponsiblePerson: () => void;
}

export function RecordAuditParticipation(props: RecordAuditParticipationProps) {
  const {professionLabelById, areProfessionsLoading, isProfessionsError} =
    useGetEmployeeWorkProfessionsListQuery(undefined, {
      selectFromResult: ({data, isLoading, isError}) => ({
        areProfessionsLoading: isLoading,
        isProfessionsError: isError,
        professionLabelById: new Map(
          getActiveOptions(data?.professions).map((option) => [option.value, option.label ?? ''])
        ),
      }),
    });

  const peopleCount =
    (isNotNil(props.auditorEmployeeId) || props.isAuditorUpdating ? 1 : 0) +
    (isNotNil(props.responsiblePersonEmployeeId) ? 1 : 0) +
    props.participants.length +
    props.loadingParticipantCount;

  const auditorName = isNotNil(props.auditorEmployeeId)
    ? (props.nameByEmployeeId.get(props.auditorEmployeeId) ?? '')
    : '';

  const content = (
    <DataStatus
      isError={isProfessionsError}
      minHeight={50}
      data-testid={suffixTestId('team-status', props)}
    >
      <VStack spacing={4}>
        <HStack justify="space-between" align="center" wrap>
          <Heading size={4}>{i18n.t('page.recordAudit.team.title')}</Heading>
          <HStack spacing={3} align="center">
            <Text size="small" color="secondary">
              {i18n.t('page.recordAudit.team.peopleCount', {count: peopleCount})}
            </Text>
            <Show when={props.isEditable}>
              <Button
                variant="link"
                leftIcon="social/person_add"
                title={i18n.t('general.actions.add')}
                onClick={props.onAddParticipant}
                data-testid={suffixTestId('team-add', props)}
              />
            </Show>
          </HStack>
        </HStack>

        <Grid columns={props.columns ?? DEFAULT_GRID_COLUMNS} spacing={4}>
          {props.isEditable && !props.isAuditorUpdating && !isNotNilOrEmpty(auditorName) ? (
            <TeamMemberPlaceholder
              title={i18n.t('page.recordAudit.team.selectAuditor')}
              onClick={props.onEditAuditor}
              data-testid={suffixTestId('team-auditor', props)}
            />
          ) : (
            <RecordAuditTeamMember
              data-testid={suffixTestId('team-auditor', props)}
              name={auditorName}
              isLoading={props.isAuditorUpdating}
              roleLabels={[i18n.t('page.recordAudit.create.fields.auditor')]}
              roleColorScheme="neutral"
              action={
                props.isEditable ? (
                  <IconButton
                    icon="image/edit"
                    size="small"
                    onClick={props.onEditAuditor}
                    aria-label={i18n.t('general.actions.edit')}
                    data-testid={suffixTestId('team-auditor-edit', props)}
                  />
                ) : undefined
              }
            />
          )}
          {isNotNil(props.responsiblePersonEmployeeId) || props.isResponsiblePersonUpdating ? (
            <RecordAuditTeamMember
              data-testid={suffixTestId('team-responsiblePerson', props)}
              name={
                isNotNil(props.responsiblePersonEmployeeId)
                  ? (props.nameByEmployeeId.get(props.responsiblePersonEmployeeId) ?? '')
                  : ''
              }
              isLoading={props.isResponsiblePersonUpdating}
              roleLabels={[i18n.t('page.recordAudit.create.fields.responsiblePerson')]}
              roleColorScheme="neutral"
              action={
                props.isEditable ? (
                  <IconButton
                    icon="image/edit"
                    size="small"
                    onClick={props.onEditResponsiblePerson}
                    aria-label={i18n.t('general.actions.edit')}
                    data-testid={suffixTestId('team-responsiblePerson-edit', props)}
                  />
                ) : undefined
              }
            />
          ) : (
            <Show when={props.isEditable}>
              <TeamMemberPlaceholder
                title={i18n.t('page.recordAudit.team.selectResponsiblePerson')}
                onClick={props.onEditResponsiblePerson}
                data-testid={suffixTestId('team-responsiblePerson', props)}
              />
            </Show>
          )}
        </Grid>

        <Show when={props.participants.length > 0 || props.isEditable}>
          <Text size="small" color="tertiary">
            {i18n.t('page.recordAudit.create.fields.participants')}
          </Text>
        </Show>
        <Grid columns={props.columns ?? DEFAULT_GRID_COLUMNS} spacing={4}>
          {props.participants.map((participant, index) => (
            <RecordAuditParticipantMember
              key={`${participant.employeeId}-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
              data-testid={suffixTestId(`team-participant-[${index}]`, props)}
              employeeId={participant.employeeId}
              name={props.nameByEmployeeId.get(participant.employeeId) ?? ''}
              professionLabelById={professionLabelById}
              isProfessionsLoading={areProfessionsLoading}
              action={
                props.isEditable ? (
                  <IconButton
                    icon="action/delete"
                    size="small"
                    onClick={() => props.onRemoveParticipant(index)}
                    data-testid={suffixTestId(`team-participant-[${index}]-remove`, props)}
                  />
                ) : undefined
              }
            />
          ))}
          {Array.from({length: props.loadingParticipantCount}).map((_, index) => (
            <RecordAuditTeamMember
              key={`loading-participant-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
              data-testid={suffixTestId(`team-participant-loading-[${index}]`, props)}
              name=""
              roleColorScheme="neutral"
              isLoading
            />
          ))}
          <Show
            when={
              props.participants.length === 0 &&
              props.loadingParticipantCount === 0 &&
              props.isEditable
            }
          >
            <TeamMemberPlaceholder
              title={i18n.t('page.recordAudit.create.actions.addParticipant')}
              onClick={props.onAddParticipant}
              data-testid={suffixTestId('team-participants-add', props)}
            />
          </Show>
        </Grid>
      </VStack>
    </DataStatus>
  );

  return match(props.variant ?? 'card')
    .with('card', () => <Card data-testid={suffixTestId('team', props)}>{content}</Card>)
    .with('flat', () => <Box data-testid={suffixTestId('team', props)}>{content}</Box>)
    .exhaustive();
}
